// Checkout.jsx
// Checkout page: shipping address form + order summary.
// On submit, saves the order to Firestore and clears the cart.

import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { collection, addDoc, getDocs, getDoc, query, where, updateDoc, doc, arrayUnion, arrayRemove, runTransaction } from "firebase/firestore";
import { db } from "../firebase/config";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import {
  formatCurrency,
  calculateShipping,
  calculateTax,
  calculateTotal,
  shippingLabel,
} from "../utils/pricing";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

export default function Checkout() {
  const { cartItems, subtotal, clearCart } = useCart();
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Pre-fill from user profile if available
  const defaultAddress = userProfile?.addresses?.find((a) => a.isDefault) || userProfile?.addresses?.[0];

  const [form, setForm] = useState({
    name: userProfile?.displayName || currentUser?.displayName || "",
    email: currentUser?.email || "",
    phone: userProfile?.phone || "",
    address: defaultAddress?.street || userProfile?.address || "",
    city: defaultAddress?.city || "",
    state: defaultAddress?.state || "",
    zip: defaultAddress?.zip || "",
    country: "US",
    paymentMethod: "cod", // Cash on Delivery placeholder
  });

  const [selectedAddressId, setSelectedAddressId] = useState(defaultAddress?.id || null);

  // --- Coupon State ---
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [applyingCoupon, setApplyingCoupon] = useState(null); // ID of currently applying coupon format loader
  const [appliedCoupon, setAppliedCoupon] = useState(null); // Full verified coupon data
  const [couponError, setCouponError] = useState("");

  // Fetch Coupons
  useEffect(() => {
    if (!currentUser) return;
    async function fetchOffers() {
      try {
        const q = query(collection(db, "offers"), where("isActive", "==", true));
        const snap = await getDocs(q);
        const offersList = snap.docs.map(d => ({ id: d.id, ...d.data() }));

        // Filter: unused by this user, eligible, and not expired
        const now = new Date();
        const eligible = offersList.filter(o => {
          const isUnused = !(o.usedBy || []).includes(currentUser.uid);
          const isTargeted = o.showToAll || (o.selectedUsers || []).includes(currentUser.email);
          const notExpired = !o.expiryDate || new Date(o.expiryDate) >= now;
          const underLimit = !o.usageLimit || (o.usesCount || 0) < o.usageLimit;
          
          return isUnused && isTargeted && notExpired && underLimit;
        });
        setAvailableCoupons(eligible);
      } catch (err) {
        console.error("Error fetching coupons:", err);
      }
    }
    fetchOffers();
  }, [currentUser]);

  const handleSelectAddress = (addr) => {
    setSelectedAddressId(addr.id);
    setForm(prev => ({
      ...prev,
      address: addr.street,
      city: addr.city,
      state: addr.state,
      zip: addr.zip,
    }));
  };

  const getIconForType = (type) => {
    if (type === "Home") return <HomeOutlinedIcon sx={{ fontSize: 18 }} />;
    if (type === "Office") return <BusinessOutlinedIcon sx={{ fontSize: 18 }} />;
    return <LocationOnOutlinedIcon sx={{ fontSize: 18 }} />;
  };

  const shipping = calculateShipping(subtotal);
  const tax = calculateTax(subtotal);

  let discountAmount = 0;
  if (appliedCoupon) {
    if (appliedCoupon.discountType === "percentage") {
      discountAmount = subtotal * (appliedCoupon.discountValue / 100);
    } else {
      discountAmount = appliedCoupon.discountValue;
    }
    if (discountAmount > subtotal) discountAmount = subtotal; // Cap discount
  }

  const baseTotal = calculateTotal(subtotal);
  const finalTotal = Math.max(0, baseTotal - discountAmount);

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  // --- Apply Coupon Logic ---
  async function handleApplyCoupon(coupon) {
    setCouponError("");
    setApplyingCoupon(coupon.id);

    try {
      // 1. Re-fetch from backend to verify dynamically
      const docRef = doc(db, "offers", coupon.id);
      const snap = await getDoc(docRef);

      if (!snap.exists()) {
        setCouponError("This coupon no longer exists.");
        setAvailableCoupons(prev => prev.filter(c => c.id !== coupon.id));
        return;
      }

      const serverCoupon = { id: snap.id, ...snap.data() };

      // 2. Validate IsActive
      if (!serverCoupon.isActive) {
        setCouponError("This coupon is no longer active.");
        setAvailableCoupons(prev => prev.filter(c => c.id !== coupon.id));
        return;
      }

      // 3. Validate UsedBy
      if ((serverCoupon.usedBy || []).includes(currentUser.uid)) {
        setCouponError("You have already used this coupon.");
        setAvailableCoupons(prev => prev.filter(c => c.id !== coupon.id));
        return;
      }

      // 4. Validate MinSpend
      if (subtotal < (serverCoupon.minSpend || 0)) {
        setCouponError(`You must spend at least ${formatCurrency(serverCoupon.minSpend)} to use this coupon.`);
        return;
      }

      // 5. Validate Eligibility
      if (!serverCoupon.showToAll && !(serverCoupon.selectedUsers || []).includes(currentUser.email)) {
        setCouponError("You are not eligible for this coupon.");
        return;
      }

      // 6. Validate Expiry
      if (serverCoupon.expiryDate && new Date(serverCoupon.expiryDate) < new Date()) {
        setCouponError("This coupon has expired.");
        setAvailableCoupons(prev => prev.filter(c => c.id !== coupon.id));
        return;
      }

      // 7. Validate Usage Limit
      if (serverCoupon.usageLimit && (serverCoupon.usesCount || 0) >= serverCoupon.usageLimit) {
        setCouponError("This coupon has reached its usage limit.");
        setAvailableCoupons(prev => prev.filter(c => c.id !== coupon.id));
        return;
      }

      // Success
      setAppliedCoupon(serverCoupon);
    } catch (err) {
      console.error("Error applying coupon:", err);
      setCouponError("Failed to apply coupon. Please try again.");
    } finally {
      setApplyingCoupon(null);
    }
  }

  function handleRemoveCoupon() {
    setAppliedCoupon(null);
    setCouponError("");
  }

  // ── Submit order to Firestore ──────────────────────────────────────────────
  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    // Basic validation
    const required = ["name", "email", "address", "city", "state", "zip"];
    for (const field of required) {
      if (!form[field].trim()) {
        setError(`Please fill in: ${field}`);
        return;
      }
    }

    try {
      setSubmitting(true);

      // We use a transaction to ensure atomic order creation and coupon update
      // This prevents race conditions and ensures "Backend Revalidation" logic
      const orderId = await runTransaction(db, async (transaction) => {
        let finalDiscount = 0;
        let couponDocRef = null;
        let serverCouponData = null;

        if (appliedCoupon) {
          couponDocRef = doc(db, "offers", appliedCoupon.id);
          const couponSnap = await transaction.get(couponDocRef);

          if (!couponSnap.exists()) {
            throw new Error("Coupon no longer exists.");
          }

          serverCouponData = couponSnap.data();
          const serverCoupon = serverCouponData;
          const now = new Date();

          // Robust Re-validation
          if (!serverCoupon.isActive) throw new Error("Coupon is no longer active.");
          if ((serverCoupon.usedBy || []).includes(currentUser.uid)) throw new Error("You have already used this coupon.");
          if (subtotal < (serverCoupon.minSpend || 0)) throw new Error("Minimum spend requirement not met.");
          if (serverCoupon.expiryDate && new Date(serverCoupon.expiryDate) < now) throw new Error("Coupon has expired.");
          if (serverCoupon.usageLimit && (serverCoupon.usesCount || 0) >= serverCoupon.usageLimit) throw new Error("Coupon usage limit reached.");
          if (!serverCoupon.showToAll && !(serverCoupon.selectedUsers || []).includes(currentUser.email)) throw new Error("You are not eligible for this coupon.");

          // Calculate discount again to be safe
          if (serverCoupon.discountType === "percentage") {
            finalDiscount = subtotal * (serverCoupon.discountValue / 100);
          } else {
            finalDiscount = serverCoupon.discountValue;
          }
          if (finalDiscount > subtotal) finalDiscount = subtotal;
        }

        const finalOrderTotal = Math.max(0, (subtotal + shipping + tax) - finalDiscount);

        // 1. Create Order Doc reference
        const newOrderRef = doc(collection(db, "orders"));

        // 2. Build Order Data
        const orderData = {
          userId: currentUser.uid,
          userEmail: currentUser.email,
          items: cartItems.map((i) => ({
            id: i.id,
            name: i.name,
            price: i.price,
            quantity: i.quantity,
            image: i.image,
            category: i.category,
          })),
          shipping: {
            name: form.name,
            email: form.email,
            phone: form.phone,
            address: form.address,
            city: form.city,
            state: form.state,
            zip: form.zip,
            country: form.country,
          },
          pricing: {
            subtotal,
            shippingCost: shipping,
            tax,
            discount: finalDiscount,
            total: finalOrderTotal,
          },
          isCouponApplied: !!appliedCoupon,
          coupon: appliedCoupon ? {
            couponId: appliedCoupon.id,
            couponCode: appliedCoupon.code,
            discountType: appliedCoupon.discountType,
            discountValue: appliedCoupon.discountValue,
            discountAmount: finalDiscount
          } : null,
          paymentMethod: form.paymentMethod,
          status: "pending",
          createdAt: new Date(),
        };

        // 3. Set the order
        transaction.set(newOrderRef, orderData);

        // 4. Update coupon usage if applicable
        if (couponDocRef && serverCouponData) {
          transaction.update(couponDocRef, {
            usedBy: arrayUnion(currentUser.uid),
            selectedUsers: arrayRemove(currentUser.email),
            usesCount: (serverCouponData.usesCount || 0) + 1
          });
        }

        return newOrderRef.id;
      });

      // Clear the cart after successful order
      clearCart();

      // Navigate to order confirmation
      navigate(`/order-success/${orderId}`);
    } catch (err) {
      console.error("Order failed:", err);
      setError(err.message || "Failed to place order. Please try again.");
      
      // If coupon error identified in transaction, clear it
      if (err.message.includes("Coupon") || err.message.includes("spend") || err.message.includes("eligible")) {
        setAppliedCoupon(null);
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (cartItems.length === 0) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <p className="text-5xl mb-4">🛒</p>
        <h2 className="text-xl font-bold mb-3">Nothing to checkout</h2>
        <Link to="/" className="text-red-600 hover:underline">Go Shopping</Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Checkout</h1>

      <form onSubmit={handleSubmit} className="flex flex-col lg:flex-row gap-8">
        {/* Left: Shipping form */}
        <div className="flex-1 space-y-6">
          {/* Shipping address */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Shipping Information</h3>

            {userProfile?.addresses && userProfile.addresses.length > 0 && (
              <div className="mb-8">
                <p className="block text-sm font-medium text-gray-700 mb-3">Select Saved Address</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {userProfile.addresses.map((addr) => (
                    <div
                      key={addr.id}
                      onClick={() => handleSelectAddress(addr)}
                      className={`cursor-pointer p-4 rounded-xl border-2 transition-all ${selectedAddressId === addr.id
                        ? "border-red-500 bg-red-50"
                        : "border-gray-200 hover:border-red-200 bg-gray-50"
                        }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-gray-500">{getIconForType(addr.type)}</span>
                        <span className="font-semibold text-gray-900">{addr.type}</span>
                        {addr.isDefault && (
                          <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-red-600 bg-red-100 px-2 py-0.5 rounded-md">
                            Default
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600">
                        <p>{addr.street}</p>
                        <p>{addr.city}, {addr.state} {addr.zip}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="relative flex items-center py-6">
                  <div className="flex-grow border-t border-gray-200"></div>
                  <span className="flex-shrink-0 mx-4 text-gray-400 text-sm">or enter new address</span>
                  <div className="flex-grow border-t border-gray-200"></div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <input name="name" value={form.name} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Jane Doe" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input name="email" type="email" value={form.email} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="jane@example.com" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input name="phone" type="tel" value={form.phone} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="+1 555 000 0000" />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Street Address *</label>
                <input name="address" value={form.address} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="123 Main St" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                <input name="city" value={form.city} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="New York" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                <input name="state" value={form.state} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="NY" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code *</label>
                <input name="zip" value={form.zip} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="10001" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <select name="country" value={form.country} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 bg-white">
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                  <option value="GB">United Kingdom</option>
                  <option value="AU">Australia</option>
                  <option value="IN">India</option>
                </select>
              </div>
            </div>
          </div>

          {/* Payment method (placeholder) */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Payment Method</h3>

            <div className="space-y-3">
              {[
                { value: "cod", label: "Cash on Delivery", icon: "💵" },
                { value: "card", label: "Credit / Debit Card (coming soon)", icon: "💳", disabled: true },
                { value: "paypal", label: "PayPal (coming soon)", icon: "🅿️", disabled: true },
              ].map((opt) => (
                <label key={opt.value} className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-colors ${form.paymentMethod === opt.value ? "border-red-500 bg-red-50" : "border-gray-200 hover:border-gray-300"
                  } ${opt.disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
                  <input
                    type="radio" name="paymentMethod" value={opt.value}
                    checked={form.paymentMethod === opt.value}
                    onChange={handleChange}
                    disabled={opt.disabled}
                    className="accent-red-600"
                  />
                  <span className="text-lg">{opt.icon}</span>
                  <span className="text-sm font-medium text-gray-700">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm flex items-center">
              <InfoOutlinedIcon fontSize="small" className="mr-2" />
              {error}
            </div>
          )}
        </div>

        {/* Right: Order summary & Coupons */}
        <div className="w-full lg:w-[22rem] space-y-6 lg:sticky lg:top-24 lg:self-start">

          {/* Order Summary */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Order Summary</h3>

            {/* Items list */}
            <div className="space-y-3 mb-5 max-h-48 overflow-y-auto pr-1">
              {cartItems.map((item) => (
                <div key={item.id} className="flex justify-between text-sm text-gray-600 bg-gray-50 p-2 rounded-lg">
                  <span className="truncate flex-1 mr-2">{item.name} ×{item.quantity}</span>
                  <span className="flex-shrink-0 font-semibold">{formatCurrency(item.price * item.quantity)}</span>
                </div>
              ))}
            </div>

            <div className="border-t border-gray-100 pt-4 space-y-3 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span><span className="font-medium">{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Shipping</span>
                <span className={shipping === 0 ? "text-green-600 font-bold tracking-wide" : "font-medium"}>{shippingLabel(subtotal)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Tax (8%)</span><span className="font-medium">{formatCurrency(tax)}</span>
              </div>

              {/* Discount Row */}
              {appliedCoupon && (
                <div className="flex justify-between text-green-600 bg-green-50 p-2 rounded-md font-bold text-sm">
                  <div className="flex items-center">
                    <span>Discount ({appliedCoupon.code})</span>
                    <button type="button" onClick={handleRemoveCoupon} className="ml-2 text-xs text-red-500 hover:text-red-700 underline font-normal">Remove</button>
                  </div>
                  <span>-{formatCurrency(discountAmount)}</span>
                </div>
              )}

              <div className="flex justify-between font-extrabold text-xl pt-3 border-t border-gray-100 items-center">
                <span>Total</span>
                <span className="text-red-600">{formatCurrency(finalTotal)}</span>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full mt-6 bg-red-600 text-white py-3.5 rounded-xl font-bold text-lg hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed shadow-md hover:shadow-lg transition-all"
            >
              {submitting ? "Processing..." : `Place Order · ${formatCurrency(finalTotal)}`}
            </button>
          </div>

          {/* Coupons Section */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h3 className="text-md font-bold text-gray-900 mb-3 flex items-center gap-2">
              🎫 Apply Offers & Coupons
            </h3>

            {couponError && (
              <div className="mb-3 px-3 py-2 bg-red-50 text-red-600 text-xs rounded-lg font-medium">
                {couponError}
              </div>
            )}

            {availableCoupons.length === 0 ? (
              <p className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg text-center">No available coupons right now.</p>
            ) : (
              <div className="space-y-3">
                {availableCoupons.map((coupon) => {
                  const meetsCriteria = subtotal >= (coupon.minSpend || 0);
                  const isApplying = applyingCoupon === coupon.id;
                  const isApplied = appliedCoupon?.id === coupon.id;

                  return (
                    <div key={coupon.id} className={`border rounded-xl p-3 relative overflow-hidden transition-all ${isApplied ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                      <div className="flex justify-between items-start gap-2">
                        <div>
                          <div className="inline-block bg-purple-100 text-purple-800 text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded border border-purple-200 mb-1">
                            {coupon.code}
                          </div>
                          <p className="text-sm font-semibold text-gray-900">
                            {coupon.discountType === "percentage" ? `${coupon.discountValue}% OFF` : `Flat ${formatCurrency(coupon.discountValue)} OFF`}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {coupon.minSpend > 0 ? `On orders above ${formatCurrency(coupon.minSpend)}` : "No minimum spend"}
                          </p>
                        </div>

                        {!isApplied ? (
                          <button
                            type="button" // Important to prevent form submission
                            disabled={!meetsCriteria || isApplying || appliedCoupon !== null}
                            onClick={() => handleApplyCoupon(coupon)}
                            className={`flex-shrink-0 text-xs font-bold px-3 py-1.5 rounded-lg transition-all ${meetsCriteria && !appliedCoupon
                              ? "bg-gray-900 text-white hover:bg-gray-800"
                              : "bg-gray-100 text-gray-400 cursor-not-allowed"
                              }`}
                          >
                            {isApplying ? "..." : "Apply"}
                          </button>
                        ) : (
                          <span className="text-xs font-bold text-green-600 bg-green-100 px-2 py-1 rounded-md">
                            Applied
                          </span>
                        )}
                      </div>

                      {/* Info bar if criteria strictly not met (and not already applied) */}
                      {!meetsCriteria && !isApplied && (
                        <div className="mt-2 text-[10px] sm:text-xs text-red-500 font-medium">
                          Add {formatCurrency((coupon.minSpend || 0) - subtotal)} more to unlock.
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
